import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Ошибки прав и валидации повторять бессмысленно.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Ключи запросов собираем централизованно: инвалидация после мутаций
 * должна работать по префиксу, а не по случайно совпавшим строкам.
 */
export const qk = {
  auth: {
    session: () => ['auth', 'session'] as const,
    permissions: () => ['auth', 'permissions'] as const,
  },
  subscribers: {
    all: () => ['subscribers'] as const,
    list: (params: unknown) => ['subscribers', 'list', params] as const,
    detail: (id: string) => ['subscribers', 'detail', id] as const,
    tab: (id: string, tab: string, params?: unknown) => ['subscribers', 'detail', id, tab, params] as const,
    search: (term: string) => ['subscribers', 'search', term] as const,
  },
  meters: {
    all: () => ['meters'] as const,
    list: (params: unknown) => ['meters', 'list', params] as const,
    detail: (id: string) => ['meters', 'detail', id] as const,
    verifications: (params: unknown) => ['meters', 'verifications', params] as const,
    acts: (params: unknown) => ['meters', 'acts', params] as const,
  },
  readings: {
    all: () => ['readings'] as const,
    list: (params: unknown) => ['readings', 'list', params] as const,
    routes: (params?: unknown) => ['readings', 'routes', params] as const,
    route: (id: string) => ['readings', 'route', id] as const,
    anomalies: (params: unknown) => ['readings', 'anomalies', params] as const,
    imports: (params?: unknown) => ['readings', 'imports', params] as const,
  },
  billing: {
    all: () => ['billing'] as const,
    runs: (params: unknown) => ['billing', 'runs', params] as const,
    run: (id: string) => ['billing', 'run', id] as const,
    chunks: (id: string) => ['billing', 'run', id, 'chunks'] as const,
    recalculations: (params: unknown) => ['billing', 'recalculations', params] as const,
    periods: () => ['billing', 'periods'] as const,
  },
  invoices: {
    all: () => ['invoices'] as const,
    list: (params: unknown) => ['invoices', 'list', params] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
  },
  payments: {
    all: () => ['payments'] as const,
    list: (params: unknown) => ['payments', 'list', params] as const,
    detail: (id: string) => ['payments', 'detail', id] as const,
    shifts: (params?: unknown) => ['payments', 'shifts', params] as const,
    currentShift: () => ['payments', 'shifts', 'current'] as const,
  },
  debt: {
    all: () => ['debt'] as const,
    registry: (params: unknown) => ['debt', 'registry', params] as const,
    claims: (params: unknown) => ['debt', 'claims', params] as const,
    orders: (params: unknown) => ['debt', 'orders', params] as const,
    agreements: (params: unknown) => ['debt', 'agreements', params] as const,
  },
  infrastructure: {
    all: () => ['infrastructure'] as const,
    layers: (params?: unknown) => ['infrastructure', 'layers', params] as const,
    objects: (kind: string, params?: unknown) => ['infrastructure', 'objects', kind, params] as const,
    object: (kind: string, id: string) => ['infrastructure', 'object', kind, id] as const,
  },
  dispatch: {
    all: () => ['dispatch'] as const,
    requests: (params: unknown) => ['dispatch', 'requests', params] as const,
    request: (id: string) => ['dispatch', 'request', id] as const,
    incidents: (params?: unknown) => ['dispatch', 'incidents', params] as const,
    crews: (params?: unknown) => ['dispatch', 'crews', params] as const,
    workOrders: (params?: unknown) => ['dispatch', 'work-orders', params] as const,
    shutdownImpact: (valveIds: string[]) => ['dispatch', 'shutdown-impact', valveIds] as const,
  },
  reports: {
    all: () => ['reports'] as const,
    dashboard: (params: unknown) => ['reports', 'dashboard', params] as const,
    builder: (params: unknown) => ['reports', 'builder', params] as const,
    saved: () => ['reports', 'saved'] as const,
  },
  admin: {
    all: () => ['admin'] as const,
    users: (params: unknown) => ['admin', 'users', params] as const,
    user: (id: string) => ['admin', 'user', id] as const,
    roles: () => ['admin', 'roles'] as const,
    permissions: () => ['admin', 'permissions'] as const,
    dictionaries: (kind: string, params?: unknown) => ['admin', 'dictionaries', kind, params] as const,
    tariffs: (params: unknown) => ['admin', 'tariffs', params] as const,
    settings: () => ['admin', 'settings'] as const,
    audit: (params: unknown) => ['admin', 'audit', params] as const,
  },
  notifications: {
    list: () => ['notifications'] as const,
  },
} as const;
